import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Combobox } from '../components/ui/combobox';
import {
  Pill, Package, Check, Clock, AlertCircle, Loader2,
  ClipboardList, TrendingUp, Calendar, AlertTriangle, Info, Wrench, ScanLine
} from 'lucide-react';
import { toast } from 'sonner';
import DispensacaoItemCard from '../components/dispensacao/DispensacaoItemCard';
import DispensacaoAvulsoTab from '../components/dispensacao/DispensacaoAvulsoTab';
import DispensacaoNaoPrescritoModal from '../components/dispensacao/DispensacaoNaoPrescritoModal';
import BarcodeScanner from '../components/dispensacao/BarcodeScanner';
import DispensacaoCorrecaoDialog from '../components/dispensacao/DispensacaoCorrecaoDialog';

const Dispensacao = () => {
  const { api, user, can } = useAuth();
  const podeCorrigir = can('corrigir_dispensacao');
  const [correcao, setCorrecao] = useState(null);

  // State
  const [internacoes, setInternacoes] = useState([]);
  const [selectedInternacao, setSelectedInternacao] = useState('');
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [sheet, setSheet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null);
  const [prnQuantities, setPrnQuantities] = useState({});
  const [prnObs, setPrnObs] = useState({});

  // Non-prescribed
  const [showNaoPrescritoModal, setShowNaoPrescritoModal] = useState(false);
  const [estoqueItens, setEstoqueItens] = useState([]);
  const [savingNP, setSavingNP] = useState(false);

  const isAdmin = ['admin', 'master_admin', 'Master Admin'].includes(user?.perfil);

  // Avulsos state
  const [avulsoList, setAvulsoList] = useState({ ambulancias: [], base: [] });
  const [avulsoLoading, setAvulsoLoading] = useState(false);
  const [cartAmbulancias, setCartAmbulancias] = useState([]);
  const [cartBase, setCartBase] = useState([]);
  const [avulsoForm, setAvulsoForm] = useState({
    item_id: '', quantidade: 1, observacoes: '', internacao_id: '', tecnico_nome: ''
  });
  const [savingAvulso, setSavingAvulso] = useState(false);
  const [usuarios, setUsuarios] = useState([]);

  // Barcode scanner state
  const [scanResult, setScanResult] = useState(null);
  const [scanLoading, setScanLoading] = useState(false);

  // Fetch internações
  useEffect(() => {
    const fetchInternacoes = async () => {
      try {
        const res = await api.get('/internacoes');
        setInternacoes(res.data.filter(i => i.status_kanban !== 'alta_efetivada'));
      } catch { toast.error('Erro ao carregar internações'); }
    };
    fetchInternacoes();
  }, [api]);

  // Fetch sheet
  const fetchSheet = useCallback(async () => {
    if (!selectedInternacao || !selectedDate) return;
    setLoading(true);
    try {
      const res = await api.get(`/dispensacao/${selectedInternacao}?data=${selectedDate}`);
      setSheet(res.data);
      setPrnQuantities({});
      setPrnObs({});
    } catch { setSheet(null); } finally { setLoading(false); }
  }, [api, selectedInternacao, selectedDate]);

  useEffect(() => { fetchSheet(); }, [fetchSheet]);

  // Barcode scan handler - looks up SKU, auto-fills item in avulso form
  const handleBarcodeScan = useCallback(async (code) => {
    setScanLoading(true);
    setScanResult(null);
    try {
      const res = await api.get(`/sku/lookup/${encodeURIComponent(code.trim())}`);
      const item = res.data;
      setAvulsoForm(f => ({ ...f, item_id: item.id, quantidade: 1 }));
      setScanResult({
        success: true,
        message: `${item.nome} — Estoque: ${item.estoque_atual} ${item.unidade}`,
        sku: item.sku,
      });
      toast.success(`${item.nome} selecionado via scanner`);
    } catch (err) {
      setScanResult({
        success: false,
        message: err.response?.status === 404 ? 'Item não encontrado para este código' : 'Erro na busca',
      });
    } finally { setScanLoading(false); }
  }, [api, setAvulsoForm]);

  // Patient-specific scan handler - finds item in the prescription sheet and auto-dispenses
  const handlePatientBarcodeScan = useCallback(async (code) => {
    setScanLoading(true);
    setScanResult(null);
    try {
      const res = await api.get(`/sku/lookup/${encodeURIComponent(code.trim())}`);
      const item = res.data;
      if (!sheet) {
        setScanResult({ success: false, message: 'Selecione um paciente primeiro' });
        return;
      }
      const allItems = [...(sheet.medicacoes || []), ...(sheet.materiais || [])];
      const sheetItem = allItems.find(si => si.item_id === item.id);
      if (sheetItem) {
        if (sheetItem.tipo_uso === 'programado') {
          const pendingSlot = sheetItem.slots.find(s => s.status === 'pendente');
          if (pendingSlot) {
            const key = `${sheetItem.regra_id}_${pendingSlot.slot_index}`;
            setSaving(key);
            await api.post('/dispensacao', {
              internacao_id: selectedInternacao, regra_consumo_id: sheetItem.regra_id,
              item_id: sheetItem.item_id, data: selectedDate, tipo_administracao: 'programado',
              slot_index: pendingSlot.slot_index, status: 'administrado', quantidade: pendingSlot.quantidade,
            });
            await fetchSheet();
            setSaving(null);
            setScanResult({
              success: true,
              message: `${item.nome} — Dose ${pendingSlot.slot_index + 1} marcada como administrada`,
              sku: item.sku,
            });
            toast.success(`Dose ${pendingSlot.slot_index + 1} de ${item.nome} administrada`);
          } else {
            setScanResult({ success: true, message: `${item.nome} — Todas as doses já administradas`, sku: item.sku });
          }
        } else {
          setScanResult({ success: true, message: `${item.nome} (PRN) — Use o botão "Registrar uso" abaixo`, sku: item.sku });
        }
      } else {
        setScanResult({ success: false, message: `${item.nome} não está na prescrição deste paciente`, sku: item.sku });
      }
    } catch (err) {
      setScanResult({
        success: false,
        message: err.response?.status === 404 ? 'Item não encontrado para este código' : 'Erro na busca',
      });
    } finally { setScanLoading(false); }
  }, [api, sheet, selectedInternacao, selectedDate, fetchSheet, setSaving]);

  // Fetch estoque
  const fetchEstoqueItens = useCallback(async () => {
    try {
      const res = await api.get('/estoque/itens');
      setEstoqueItens(res.data.filter(i => i.ativo !== false && i.estoque_atual > 0));
    } catch { /* ignore */ }
  }, [api]);

  // Fetch avulsos
  const fetchAvulsos = useCallback(async () => {
    setAvulsoLoading(true);
    try {
      const res = await api.get(`/dispensacao/avulsos?data=${selectedDate}`);
      const data = res.data;
      setAvulsoList({
        ambulancias: (data.dispensacoes || []).filter(d => d.categoria_avulso === 'ambulancias'),
        base: (data.dispensacoes || []).filter(d => d.categoria_avulso === 'base'),
        resumo: data.resumo || { total: 0 }
      });
    } catch { /* ignore */ } finally { setAvulsoLoading(false); }
  }, [api, selectedDate]);

  const fetchUsuarios = useCallback(async () => {
    try { const res = await api.get('/usuarios/resumo'); setUsuarios(res.data || []); } catch { /* ignore */ }
  }, [api]);

  useEffect(() => { fetchAvulsos(); }, [fetchAvulsos]);
  useEffect(() => { fetchUsuarios(); fetchEstoqueItens(); }, [fetchUsuarios, fetchEstoqueItens]);

  // Patient options
  const pacienteOptions = internacoes.map(i => ({
    value: i.id,
    label: i.paciente_nome || 'Paciente sem nome',
    subtitle: i.equipe_nome || undefined,
  }));
  const abrirCorrecao = podeCorrigir ? (reg) => setCorrecao({ ...reg, data: selectedDate, paciente_nome: sheet?.paciente_nome }) : null;

  // Handlers
  const handleToggleSlot = async (item, slot) => {
    const newStatus = slot.status === 'administrado' ? 'nao_administrado' : 'administrado';
    const key = `${item.regra_id}_${slot.slot_index}`;
    setSaving(key);
    try {
      await api.post('/dispensacao', {
        internacao_id: selectedInternacao, regra_consumo_id: item.regra_id,
        item_id: item.item_id, data: selectedDate, tipo_administracao: 'programado',
        slot_index: slot.slot_index, status: newStatus, quantidade: slot.quantidade,
      });
      await fetchSheet();
      toast.success(newStatus === 'administrado' ? 'Dose marcada como administrada' : 'Dose desmarcada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao registrar');
    } finally { setSaving(null); }
  };

  const handleRegistrarPRN = async (item) => {
    const qty = prnQuantities[item.regra_id] || item.quantidade_por_aplicacao || 1;
    const obs = prnObs[item.regra_id] || '';
    setSaving(`prn_${item.regra_id}`);
    try {
      await api.post('/dispensacao/prn', {
        internacao_id: selectedInternacao, regra_consumo_id: item.regra_id,
        item_id: item.item_id, data: selectedDate, quantidade: parseFloat(qty), observacoes: obs,
      });
      await fetchSheet();
      setPrnQuantities(prev => ({ ...prev, [item.regra_id]: '' }));
      setPrnObs(prev => ({ ...prev, [item.regra_id]: '' }));
      toast.success('Uso PRN registrado com sucesso');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao registrar uso PRN');
    } finally { setSaving(null); }
  };

  const handleAddToCart = (categoria) => {
    if (!avulsoForm.item_id || !avulsoForm.quantidade || avulsoForm.quantidade <= 0) {
      toast.error(!avulsoForm.item_id ? 'Selecione um item' : 'Informe a quantidade');
      return;
    }
    const item = estoqueItens.find(i => i.id === avulsoForm.item_id);
    const entry = {
      _cartId: Date.now() + Math.random(),
      item_id: avulsoForm.item_id,
      item_nome: item?.nome || '',
      item_unidade: item?.unidade || 'un',
      item_tipo: item?.tipo || 'material',
      quantidade: parseFloat(avulsoForm.quantidade),
      observacoes: avulsoForm.observacoes?.trim() || '',
      internacao_id: avulsoForm.internacao_id || '',
      paciente_label: avulsoForm.internacao_id
        ? pacienteOptions.find(p => p.value === avulsoForm.internacao_id)?.label || ''
        : '',
    };
    if (categoria === 'ambulancias') setCartAmbulancias(prev => [...prev, entry]);
    else setCartBase(prev => [...prev, entry]);
    setAvulsoForm(f => ({ ...f, item_id: '', quantidade: 1, observacoes: '' }));
    toast.success(`${entry.item_nome} adicionado ao carrinho`);
  };

  const handleRemoveFromCart = (categoria, cartId) => {
    if (categoria === 'ambulancias') setCartAmbulancias(prev => prev.filter(i => i._cartId !== cartId));
    else setCartBase(prev => prev.filter(i => i._cartId !== cartId));
  };

  const handleFinalizarDispensacao = async (categoria) => {
    const cart = categoria === 'ambulancias' ? cartAmbulancias : cartBase;
    if (cart.length === 0) { toast.error('Adicione ao menos um item'); return; }
    if (!avulsoForm.tecnico_nome?.trim()) { toast.error('Informe o técnico responsável'); return; }
    const dataDisp = avulsoForm.data || selectedDate;
    if (dataDisp > new Date().toISOString().split('T')[0]) { toast.error('Não é permitido dispensar em data futura'); return; }
    setSavingAvulso(true);
    let sucesso = 0, erros = 0;
    for (const entry of cart) {
      try {
        await api.post('/dispensacao/avulso', {
          item_id: entry.item_id, quantidade: entry.quantidade, observacoes: entry.observacoes,
          internacao_id: entry.internacao_id || null, tecnico_nome: avulsoForm.tecnico_nome.trim(),
          categoria_avulso: categoria, data: dataDisp,
        });
        sucesso++;
      } catch { erros++; }
    }
    if (sucesso > 0) {
      toast.success(`${sucesso} item(ns) dispensado(s) em ${new Date(dataDisp + 'T12:00:00').toLocaleDateString('pt-BR')}${erros > 0 ? ` (${erros} erro(s))` : ''}`);
      if (categoria === 'ambulancias') setCartAmbulancias([]);
      else setCartBase([]);
      setAvulsoForm(f => ({ ...f, item_id: '', quantidade: 1, observacoes: '', data: '' }));
      if (dataDisp !== selectedDate) setSelectedDate(dataDisp); else fetchAvulsos();
      fetchEstoqueItens();
    } else { toast.error('Erro ao dispensar itens'); }
    setSavingAvulso(false);
  };

  const handleDispensarNaoPrescrito = async (cart, justificativa, data) => {
    if (cart.length === 0 || justificativa.length < 5) return;
    const dataDisp = data || selectedDate;
    setSavingNP(true);
    let sucesso = 0, erros = 0, ultimoErro = '';
    for (const entry of cart) {
      try {
        await api.post('/dispensacao/nao-prescrito', {
          internacao_id: selectedInternacao, item_id: entry.item_id,
          quantidade: entry.quantidade, justificativa, data: dataDisp,
        });
        sucesso++;
      } catch (err) { erros++; ultimoErro = err.response?.data?.detail || ''; }
    }
    setSavingNP(false);
    if (sucesso > 0) {
      toast.success(`${sucesso} item(ns) dispensado(s) em ${new Date(dataDisp + 'T12:00:00').toLocaleDateString('pt-BR')}${erros > 0 ? ` (${erros} erro(s))` : ''}`);
      setShowNaoPrescritoModal(false);
      if (dataDisp !== selectedDate) setSelectedDate(dataDisp); else await fetchSheet();
    } else { toast.error(ultimoErro || 'Erro ao dispensar itens'); }
  };

  // Reconciliation helper
  const renderReconciliation = () => {
    if (!sheet || (sheet.medicacoes.length === 0 && sheet.materiais.length === 0)) return null;
    const allItems = [...sheet.medicacoes, ...sheet.materiais];
    const naoDispensados = allItems.filter(m => m.tipo_uso === 'programado' && m.slots.some(s => s.status === 'pendente'));
    const prnUsados = allItems.filter(m => m.tipo_uso === 'se_necessario' && m.prn_registros.length > 0);

    return (
      <Card className="border-dashed">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-amber-500" />
            Reconciliacao
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-1">
          {naoDispensados.length > 0 && (
            <div className="flex items-start gap-2 text-amber-700">
              <Clock className="h-4 w-4 mt-0.5 shrink-0" />
              <span><strong>{naoDispensados.length} item(ns) programado(s)</strong> com doses pendentes: {naoDispensados.map(m => m.item_nome).join(', ')}</span>
            </div>
          )}
          {prnUsados.length > 0 && (
            <div className="flex items-start gap-2 text-green-700">
              <Check className="h-4 w-4 mt-0.5 shrink-0" />
              <span><strong>{prnUsados.length} item(ns) PRN</strong> dispensado(s) hoje: {prnUsados.map(m => m.item_nome).join(', ')}</span>
            </div>
          )}
          {naoDispensados.length === 0 && prnUsados.length === 0 && (
            <p className="text-muted-foreground">Nenhum item pendente ou PRN dispensado hoje.</p>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Layout>
      <div className="container mx-auto py-6 px-4 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight" data-testid="dispensacao-title">Dispensação</h1>
            <p className="text-muted-foreground">Registre medicações e materiais realmente administrados por paciente/dia</p>
          </div>
          {selectedInternacao && sheet && (
            <Button variant="outline" className="gap-2 border-red-300 text-red-700 hover:bg-red-50"
              onClick={() => { fetchEstoqueItens(); setShowNaoPrescritoModal(true); }}
              data-testid="btn-dispensar-nao-prescrito"
            >
              <AlertTriangle className="h-4 w-4" />
              Dispensar item não prescrito
            </Button>
          )}
        </div>

        {/* Info note */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-md px-4 py-2" data-testid="nota-dispensacao">
          <Info className="h-4 w-4 text-blue-500 shrink-0" />
          <span>O estoque é baixado somente após registro de dispensação.</span>
        </div>

        {/* Top-level mode tabs */}
        <Tabs defaultValue="paciente" className="space-y-4" onValueChange={() => setScanResult(null)}>
          <TabsList>
            <TabsTrigger value="paciente" data-testid="tab-mode-paciente">
              <ClipboardList className="h-4 w-4 mr-2" /> Por Paciente
            </TabsTrigger>
            <TabsTrigger value="ambulancias" data-testid="tab-mode-ambulancias">
              <Wrench className="h-4 w-4 mr-2" /> MAT/MED Ambulâncias ({avulsoList?.ambulancias?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="base" data-testid="tab-mode-base">
              <Package className="h-4 w-4 mr-2" /> MAT/MED Base ({avulsoList?.base?.length || 0})
            </TabsTrigger>
          </TabsList>

          {/* Por Paciente */}
          <TabsContent value="paciente" className="space-y-4">
            <Card>
              <CardContent className="pt-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1 space-y-1">
                    <Label className="text-sm font-medium">Selecionar paciente</Label>
                    <Combobox
                      options={pacienteOptions} value={selectedInternacao}
                      onValueChange={setSelectedInternacao} placeholder="Buscar paciente..."
                      searchPlaceholder="Digite o nome do paciente..." emptyText="Nenhum paciente encontrado"
                      data-testid="select-paciente-dispensacao"
                    />
                  </div>
                  <div className="space-y-1 w-48">
                    <Label className="text-sm font-medium flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> Data</Label>
                    <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} data-testid="input-data-dispensacao" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {!selectedInternacao ? (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  <ClipboardList className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p className="font-medium">Selecione um paciente para iniciar</p>
                  <p className="text-sm">Escolha o paciente e a data para ver e registrar dispensações</p>
                </CardContent>
              </Card>
            ) : loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : sheet ? (
              <>
                {/* Barcode Scanner for patient dispensing */}
                <Card className="border-2 border-dashed border-primary/20 bg-primary/5">
                  <CardContent className="pt-4 pb-3">
                    <div className="flex items-center gap-2 mb-2">
                      <ScanLine className="h-4 w-4 text-primary" />
                      <span className="text-sm font-medium">Dispensar por código de barras</span>
                      <span className="text-xs text-muted-foreground">— escaneie para marcar a próxima dose pendente</span>
                    </div>
                    <BarcodeScanner
                      onScan={handlePatientBarcodeScan}
                      result={scanResult}
                      loading={scanLoading}
                      placeholder="Escaneie o SKU do medicamento/material..."
                    />
                  </CardContent>
                </Card>
                {/* Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Card><CardContent className="pt-4 pb-3">
                    <p className="text-xs text-muted-foreground">Total de itens</p>
                    <p className="text-2xl font-bold" data-testid="resumo-total-itens">{sheet.resumo.total_itens}</p>
                  </CardContent></Card>
                  <Card><CardContent className="pt-4 pb-3">
                    <p className="text-xs text-muted-foreground">Dispensados</p>
                    <p className="text-2xl font-bold text-green-600" data-testid="resumo-dispensados">{sheet.resumo.total_dispensado}</p>
                  </CardContent></Card>
                  <Card><CardContent className="pt-4 pb-3">
                    <p className="text-xs text-muted-foreground">Pendentes</p>
                    <p className="text-2xl font-bold text-amber-600" data-testid="resumo-pendentes">{sheet.resumo.total_pendente}</p>
                  </CardContent></Card>
                  {isAdmin && (
                    <Card><CardContent className="pt-4 pb-3">
                      <p className="text-xs text-muted-foreground flex items-center gap-1"><TrendingUp className="h-3 w-3" /> Custo do dia</p>
                      <p className="text-2xl font-bold text-blue-600" data-testid="resumo-custo">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(sheet.resumo.custo_dia || 0)}
                      </p>
                    </CardContent></Card>
                  )}
                </div>

                {/* Med / Mat tabs */}
                <Tabs defaultValue="medicacoes" className="space-y-4">
                  <TabsList>
                    <TabsTrigger value="medicacoes" data-testid="tab-medicacoes"><Pill className="h-4 w-4 mr-2" /> Medicações ({sheet.medicacoes.length})</TabsTrigger>
                    <TabsTrigger value="materiais" data-testid="tab-materiais"><Package className="h-4 w-4 mr-2" /> Materiais ({sheet.materiais.length})</TabsTrigger>
                  </TabsList>

                  <TabsContent value="medicacoes">
                    {sheet.medicacoes.length === 0 ? (
                      <Card><CardContent className="py-8 text-center text-muted-foreground"><Pill className="h-10 w-10 mx-auto mb-2 opacity-50" /><p>Nenhuma medicação prescrita para esta data</p></CardContent></Card>
                    ) : (
                      <div>{sheet.medicacoes.map(item => (
                        <DispensacaoItemCard key={item.regra_id} item={item} saving={saving} setSaving={setSaving}
                          prnQuantities={prnQuantities} setPrnQuantities={setPrnQuantities}
                          prnObs={prnObs} setPrnObs={setPrnObs}
                          onToggleSlot={handleToggleSlot} onRegistrarPRN={handleRegistrarPRN} onCorrigir={abrirCorrecao}
                        />
                      ))}</div>
                    )}
                  </TabsContent>

                  <TabsContent value="materiais">
                    {sheet.materiais.length === 0 ? (
                      <Card><CardContent className="py-8 text-center text-muted-foreground"><Package className="h-10 w-10 mx-auto mb-2 opacity-50" /><p>Nenhum material prescrito para esta data</p></CardContent></Card>
                    ) : (
                      <div>{sheet.materiais.map(item => (
                        <DispensacaoItemCard key={item.regra_id} item={item} saving={saving} setSaving={setSaving}
                          prnQuantities={prnQuantities} setPrnQuantities={setPrnQuantities}
                          prnObs={prnObs} setPrnObs={setPrnObs}
                          onToggleSlot={handleToggleSlot} onRegistrarPRN={handleRegistrarPRN} onCorrigir={abrirCorrecao}
                        />
                      ))}</div>
                    )}
                  </TabsContent>
                </Tabs>

                {renderReconciliation()}
              </>
            ) : null}
          </TabsContent>

          {/* MAT/MED Ambulâncias */}
          <TabsContent value="ambulancias" className="space-y-4">
            <DispensacaoAvulsoTab
              categoria="ambulancias" cart={cartAmbulancias}
              records={avulsoList.ambulancias || []} loading={avulsoLoading}
              estoqueItens={estoqueItens} pacienteOptions={pacienteOptions}
              usuarios={usuarios} avulsoForm={avulsoForm} setAvulsoForm={setAvulsoForm}
              savingAvulso={savingAvulso} onAddToCart={handleAddToCart}
              onRemoveFromCart={handleRemoveFromCart} onFinalizarDispensacao={handleFinalizarDispensacao}
              selectedDate={selectedDate} setSelectedDate={setSelectedDate} onRefresh={fetchAvulsos}
              onBarcodeScan={handleBarcodeScan}
              scanResult={scanResult} scanLoading={scanLoading}
              onCorrigir={podeCorrigir ? setCorrecao : null}
            />
          </TabsContent>

          {/* MAT/MED Base */}
          <TabsContent value="base" className="space-y-4">
            <DispensacaoAvulsoTab
              categoria="base" cart={cartBase}
              records={avulsoList.base || []} loading={avulsoLoading}
              estoqueItens={estoqueItens} pacienteOptions={pacienteOptions}
              usuarios={usuarios} avulsoForm={avulsoForm} setAvulsoForm={setAvulsoForm}
              savingAvulso={savingAvulso} onAddToCart={handleAddToCart}
              onRemoveFromCart={handleRemoveFromCart} onFinalizarDispensacao={handleFinalizarDispensacao}
              selectedDate={selectedDate} setSelectedDate={setSelectedDate} onRefresh={fetchAvulsos}
              onBarcodeScan={handleBarcodeScan}
              scanResult={scanResult} scanLoading={scanLoading}
              onCorrigir={podeCorrigir ? setCorrecao : null}
            />
          </TabsContent>
        </Tabs>

        <DispensacaoCorrecaoDialog registro={correcao} estoqueItens={estoqueItens} onClose={() => setCorrecao(null)} onSaved={() => { fetchSheet(); fetchAvulsos(); }} />

        {/* Non-prescribed modal */}
        <DispensacaoNaoPrescritoModal
          open={showNaoPrescritoModal}
          onOpenChange={setShowNaoPrescritoModal}
          estoqueItens={estoqueItens}
          onConfirm={handleDispensarNaoPrescrito}
          saving={savingNP}
          dataPadrao={selectedDate}
          pacienteNome={sheet?.paciente_nome}
        />
      </div>
    </Layout>
  );
};

export default Dispensacao;
